import { MtxButton, MtxIcon } from '@meilleurtaux/mtp-uikit-react';

import { NotificationItemDTOCategory, NotificationItemDTOStatus } from '@/api';
import MegaPhoneIcon from '@/assets/icons/megaPhone.svg?react';
import { getNotificationVisual } from '@/components/NotificationCenter/notificationVisual';
import { Skeleton } from '@/components/skeleton/Skeleton';
import { formatNotificationDate } from '@/utils/functions/formatNotificationDate/formatNotificationDate';
import joinClassNames from '@/utils/functions/joinClassNames/joinClassNames';
import handleNotificationLinkClick from '@/utils/functions/tracking/handleNotificationLinkClick/handleNotificationLinkClick';

import styles from './styles.module.css';

const CARD_VARIANT_CLASS = {
  info: styles.notificationCardInfo,
  alert: styles.notificationCardAlert,
  offers: styles.notificationCardOffers,
};

interface NotificationCardProps {
  title: string;
  message: string;
  link?: string | null;
  linkLabel?: string | null;
  openInNewTab?: boolean | null;
  creationDate?: string;
  status?: NotificationItemDTOStatus | null;
  category?: NotificationItemDTOCategory | null;
  isLoading?: boolean;
}

const NotificationCard = ({
  title,
  message,
  link,
  linkLabel,
  openInNewTab,
  creationDate,
  status,
  category,
  isLoading,
}: NotificationCardProps) => {
  if (isLoading) {
    return (
      <>
        <div className={styles.notificationCard}>
          <Skeleton height={18} width="40%" />
          <Skeleton height={12} width="100%" />
        </div>
        <div className={styles.notificationCard}>
          <Skeleton height={18} width="40%" />
          <Skeleton height={12} width="100%" />
        </div>
      </>
    );
  }

  const visual = getNotificationVisual(status, category);
  const cardClassName = joinClassNames(styles.notificationCard, CARD_VARIANT_CLASS[visual.variant]);

  const badge = (
    <div className={styles.notificationBadge}>
      {visual.kind === 'megaphone' ? (
        <MegaPhoneIcon className={styles.notificationIcon} />
      ) : (
        <i className={`totem-icon totem-icon-${visual.iconId} ${styles.notificationIcon}`} />
      )}
    </div>
  );

  const content = (
    <div className={styles.notificationCardContent}>
      <h3>{title}</h3>
      <p>{message}</p>
      {creationDate && <p className={styles.notificationDate}>{formatNotificationDate(creationDate)}</p>}
      {link && linkLabel && (
        <div className={styles.notificationCta}>
          <MtxButton
            variant="primary"
            size="small"
            onClick={() => handleNotificationLinkClick({ title, link, openInNewTab })}
          >
            {linkLabel}
          </MtxButton>
        </div>
      )}
    </div>
  );

  // A CTA button already carries the action — the card stays static to avoid a button-in-button.
  if (link && !linkLabel) {
    return (
      <button
        type="button"
        className={cardClassName}
        onClick={() => handleNotificationLinkClick({ title, link, openInNewTab })}
      >
        {badge}
        {content}
        <MtxIcon className={styles.chevron} name="chevron-right-big" size={24} />
      </button>
    );
  }

  return (
    <div className={cardClassName}>
      {badge}
      {content}
    </div>
  );
};

export { NotificationCard };
